import React from 'react';
import { MenuItem } from '@mui/material';
import { SalesOrderFilter } from '../../../../types/sales.types';
import { useOrdersFilters } from './OrdersFilters.hooks';
import { BRANCH_RECEIPT_NUMBERING_ENABLED } from '../../../../config/feature-flags';
import {
  BRANCH_RECEIPT_NUMBER_LABEL,
  SALES_ORDER_NUMBER_LABEL,
} from '../../../../utils/sales/branch-receipt-numbering';
import {
  ListPageFilterContainer,
  ListPagePrimaryFilterRow,
  ListPageFilterCollapse,
  ListPageFilterTextField,
  ListPageQuadSecondaryFilterRow,
  LocationScopeControl,
  ListPageActiveFilters,
} from '../../../common/list-page';

export interface OrdersFiltersProps {
  filters: SalesOrderFilter;
  setFilters: (filters: SalesOrderFilter) => void;
  onSearch: () => void;
  onReset: () => void;
  locations: { id: number; name: string }[];
  isLoading?: boolean;
  isLocationLocked?: boolean;
  onLocationScopeChange?: (payload: { locationId: string; locationName: string }) => void;
}

const OrdersFilters: React.FC<OrdersFiltersProps> = ({
  filters,
  setFilters,
  onSearch,
  onReset,
  locations,
  isLoading = false,
  isLocationLocked = false,
  onLocationScopeChange,
}) => {
  const {
    localFilters,
    showMoreFilters,
    statusOptions,
    handleChange,
    handleLocationScopeChange,
    handleOrderCodeChange,
    handleBranchReceiptCodeChange,
    handleCustomerSearchTermChange,
    handleCustomerPhoneChange,
    handleKeyDown,
    handleReset,
    handleToggleMoreFilters,
    handleSearch,
    activeFilterChips,
  } = useOrdersFilters({
    filters,
    setFilters,
    onSearch,
    onReset,
    isLocationLocked,
    onLocationScopeChange,
  });

  return (
    <ListPageFilterContainer>
      <ListPagePrimaryFilterRow
        showMoreFilters={showMoreFilters}
        onToggleMoreFilters={handleToggleMoreFilters}
        onReset={handleReset}
        onSearch={handleSearch}
        betweenResetAndSearch={
          <LocationScopeControl
            locations={locations}
            value={localFilters.locationId?.toString() || ''}
            locked={isLocationLocked}
            disabled={isLoading}
            onChange={handleLocationScopeChange}
          />
        }
        searchFieldProps={{
          label: SALES_ORDER_NUMBER_LABEL,
          name: 'orderCode',
          value: localFilters.orderCode || '',
          onChange: handleOrderCodeChange,
          onKeyDown: handleKeyDown,
          placeholder: 'Buscar por número de venta',
          disabled: isLoading,
        }}
        secondarySearchFieldProps={{
          label: 'Buscar cliente',
          name: 'customerSearchTerm',
          value: localFilters.customerSearchTerm || '',
          onChange: handleCustomerSearchTermChange,
          onKeyDown: handleKeyDown,
          placeholder: 'Nombre del cliente',
          disabled: isLoading,
        }}
      />

      <ListPageFilterCollapse in={showMoreFilters} timeout={350} unmountOnExit>
        <ListPageQuadSecondaryFilterRow>
          <ListPageFilterTextField
            fullWidth
            select
            variant="outlined"
            size="medium"
            label="Estado"
            name="status"
            value={localFilters.status || ''}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </ListPageFilterTextField>

          <ListPageFilterTextField
            fullWidth
            type="date"
            variant="outlined"
            size="medium"
            label="Venta desde"
            name="startDate"
            value={localFilters.startDate || ''}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />

          <ListPageFilterTextField
            fullWidth
            type="date"
            variant="outlined"
            size="medium"
            label="Venta hasta"
            name="endDate"
            value={localFilters.endDate || ''}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />

          {BRANCH_RECEIPT_NUMBERING_ENABLED ? (
            <ListPageFilterTextField
              fullWidth
              variant="outlined"
              size="medium"
              label={BRANCH_RECEIPT_NUMBER_LABEL}
              name="branchReceiptCode"
              value={localFilters.branchReceiptCode ?? ''}
              onChange={handleBranchReceiptCodeChange}
              onKeyDown={handleKeyDown}
              placeholder={`Buscar por ${BRANCH_RECEIPT_NUMBER_LABEL.toLowerCase()}`}
              disabled={isLoading}
              InputLabelProps={{ shrink: true }}
            />
          ) : null}

          <ListPageFilterTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Teléfono"
            name="customerPhone"
            value={localFilters.customerPhone ?? ''}
            onChange={handleCustomerPhoneChange}
            onKeyDown={handleKeyDown}
            placeholder="Buscar por teléfono"
            disabled={isLoading}
            InputLabelProps={{ shrink: true }}
          />
        </ListPageQuadSecondaryFilterRow>
      </ListPageFilterCollapse>

      <ListPageActiveFilters chips={activeFilterChips} />
    </ListPageFilterContainer>
  );
};

export default OrdersFilters;
